import { ReactNode } from 'react';
import styled from 'styled-components';

import Foldout from 'components/shared/Foldout';
import useIsMobile from 'hooks/useIsMobile';
import { colors } from 'styles/theme/colors';
import ArrowLeft from 'svg/ArrowLeft';

// A right-hand slideout shell for inbound-referral detail views (eligibility check today; denial
// / sex-offender detail next). Built on the shared Foldout, it owns the header chrome — back
// arrow, title, optional subtitle, an optional right-aligned action slot, and a close × — and a
// scrolling body. Section-specific content is passed as children.

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  // Right-aligned header slot (e.g. a Download button). Sits left of the close ×.
  headerAction?: ReactNode;
  // The back arrow duplicates the close × — both just close — so surfaces whose design draws
  // only the × can drop it.
  hideBackButton?: boolean;
  // Fixed under the header, above the scrolling body: the coverage tab strip (OLIO-2794). Outside
  // the body on purpose — switching coverage has to stay reachable however far down a long result
  // the reader has scrolled.
  tabs?: ReactNode;
  // Pinned to the bottom, for actions that belong to whatever the body is showing (when it last ran,
  // Download PDF, Re-Run). Same reason as the tabs: a benefits result is long enough that a footer
  // inside the scroll would only be found by reaching the end of it.
  footer?: ReactNode;
  children: ReactNode;
}

export default function DetailSlideout({
  open,
  onClose,
  title,
  subtitle,
  headerAction,
  hideBackButton,
  tabs,
  footer,
  children,
}: Props) {
  const isMobile = useIsMobile();

  return (
    <Foldout open={open} onBackdropClick={onClose} width={isMobile ? '100%' : 'min(95%, 1040px)'}>
      <Container>
        <HeaderBar>
          {!hideBackButton && (
            <BackButton type='button' aria-label='Back' onClick={onClose}>
              <ArrowLeft />
            </BackButton>
          )}
          <TitleSection>
            <Title>{title}</Title>
            {subtitle !== undefined && <Subtitle>{subtitle}</Subtitle>}
          </TitleSection>
          <HeaderActions>
            {headerAction}
            <CloseButton type='button' aria-label='Close' onClick={onClose}>
              {'×'}
            </CloseButton>
          </HeaderActions>
        </HeaderBar>
        {tabs && <TabBar data-testid='slideout-tabs'>{tabs}</TabBar>}
        <Body>{children}</Body>
        {footer && <FooterBar data-testid='slideout-footer'>{footer}</FooterBar>}
      </Container>
    </Foldout>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid ${colors.black10};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 4px 8px;
  margin-left: -8px;
  flex-shrink: 0;
  color: ${colors.black100};
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  color: ${colors.black100};
`;

const Subtitle = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: ${colors.black50};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
  line-height: 1;
  color: ${colors.black75};
  padding: 4px 8px;
`;

const TabBar = styled.div`
  padding: 0 24px;
  border-bottom: 1px solid ${colors.black10};
  flex-shrink: 0;
`;

const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.black10};
  background: ${colors.white};
  flex-shrink: 0;
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  /* Sections keep their natural height so the body scrolls to fit. Without this, a child with
     overflow:hidden gets min-height:0 and the flex column shrinks/clips it to ~0 height. */
  > * {
    flex-shrink: 0;
  }
`;
